// The sky's coming events, for "Remind me" (sign-up reminders by email):
// eclipses, meteor shower peaks and planet oppositions, each with a stable id
// that a stored reminder can name and the daily mailer can look up again.
//
// Pure and DOM-free, so the viewer, the static pages and the Vercel
// functions (api/) all read the same calendar.
//
//   upcomingSkyEvents({ from, days })  -> events from `from` for `days`, by time
//   skyEventById(id)                   -> one event, or null
//   nextSkyEventFor(body, { from })    -> the next event for a body key, or null
//   skyEventsInYear(year)              -> every event in a UTC calendar year
//
// An event: { id, kind, body, title, at, short, detail, major }
//   kind  'eclipse-solar' | 'eclipse-lunar' | 'meteor' | 'opposition'
//   body  the viewer's body key: 'sun', 'moon', 'earth' (meteors), a planet
//   at    ISO time (UTC) of greatest eclipse, the shower's peak, opposition
//
// Eclipses come from NASA's tables (Espenak, eclipse.gsfc.nasa.gov decade
// pages, 2026-2032); penumbral lunar eclipses are left out, since the Moon
// only dims a little. Shower peaks are the IMO working list's solar
// longitudes, solved for each year on the app's ephemeris. Oppositions are
// found on the same ephemeris (ephemeris.js, JPL's approximate elements).

import { getPlanetPosition } from '../orbits/ephemeris.js';
import { moonEcliptic } from '../orbits/moon.js';
import { distanceFromEarthKm, formatMillionKm } from '../site/readouts.js';

const DEG = Math.PI / 180;
const DAY_MS = 86400000;
const J2000 = 2451545.0;
// NASA's tables give greatest eclipse in Terrestrial Time; UTC is behind it
// by delta T, about 69 s through these years.
const DELTA_T_S = 69;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dayText = (d) => `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
const isoDay = (d) => d.toISOString().slice(0, 10);
const jdOf = (ms) => ms / DAY_MS + 2440587.5;
const msOf = (jd) => (jd - 2440587.5) * DAY_MS;
const wrap180 = (deg) => ((((deg + 180) % 360) + 360) % 360) - 180;

// ---- Eclipses ---------------------------------------------------------------
// [greatest eclipse (TT), type, where, detail]. `where` is the short line:
// the central path for a total, annular or hybrid eclipse, else where it is
// seen at all. Magnitudes and durations are NASA's.

const SOLAR = [
  ['2026-02-17T12:13:05', 'annular', 'Antarctica',
    'The ring of fire crosses Antarctica. Southern Argentina, Chile and southern Africa see a partial eclipse.'],
  ['2026-08-12T17:47:05', 'total', 'Greenland, Iceland, Spain',
    'Totality crosses Greenland, Iceland and northern Spain and lasts up to 2 minutes 18 seconds. Europe, west Africa and northern North America see a partial eclipse.'],
  ['2027-02-06T16:00:47', 'annular', 'Chile, Argentina, South Atlantic, West Africa',
    'The ring of fire crosses Chile, Argentina and the South Atlantic, then Ivory Coast, Ghana, Togo, Benin and Nigeria late in the afternoon, and lasts up to 7 minutes 51 seconds. The rest of South America and west and southern Africa see a partial eclipse.'],
  ['2027-08-02T10:07:49', 'total', 'Spain, North Africa, Egypt',
    'Totality crosses southern Spain, Morocco, Algeria, Libya, Egypt and Arabia and lasts up to 6 minutes 23 seconds. Europe, Africa, the Middle East and western Asia see a partial eclipse.'],
  ['2028-01-26T15:08:58', 'annular', 'Ecuador, Peru, Brazil, Spain, Portugal',
    'The ring of fire crosses Ecuador, Peru and Brazil, then Portugal and Spain at sunset, and lasts up to 10 minutes 27 seconds. Eastern North America, South America, western Europe and northwest Africa see a partial eclipse.'],
  ['2028-07-22T02:56:39', 'total', 'Australia, New Zealand',
    'Totality crosses Australia and New Zealand and lasts up to 5 minutes 10 seconds. Southeast Asia and Indonesia see a partial eclipse.'],
  ['2029-01-14T17:13:47', 'partial', 'North and Central America',
    'The Moon covers up to 87% of the Sun, seen from North and Central America.'],
  ['2029-06-12T04:06:13', 'partial', 'Arctic, Scandinavia, Alaska, Canada',
    'The Moon covers up to 46% of the Sun, seen from the Arctic, Scandinavia, Alaska, northern Canada and northern Asia.'],
  ['2029-07-11T15:37:18', 'partial', 'Southern Chile and Argentina',
    'The Moon takes a small bite, up to 23% of the Sun, seen from southern Chile and Argentina.'],
  ['2029-12-05T15:03:57', 'partial', 'Southern Argentina and Chile, Antarctica',
    'The Moon covers up to 89% of the Sun, seen from southern Argentina, southern Chile and Antarctica.'],
  ['2030-06-01T06:29:13', 'annular', 'North Africa, Greece, Turkey, Russia, Japan',
    'The ring of fire crosses Algeria, Tunisia, Greece, Turkey, Russia, northern China and Japan. Europe, North Africa, the Middle East and most of Asia see a partial eclipse.'],
  ['2030-11-25T06:51:37', 'total', 'Botswana, South Africa, Australia',
    'Totality crosses Botswana, South Africa and southern Australia and lasts up to 3 minutes 44 seconds. Southern Africa, Indonesia and Australia see a partial eclipse.'],
  ['2031-05-21T07:16:04', 'annular', 'Central Africa, southern India, Malaysia, Indonesia',
    'The ring of fire crosses Angola, Zambia, Tanzania, southern India, Malaysia and Indonesia. Africa, southern Asia and Australia see a partial eclipse.'],
  ['2031-11-14T21:07:30', 'hybrid', 'Pacific, Panama',
    'A short totality, about a minute, crosses the Pacific and reaches land in Panama. The southern United States, Central America and northwest South America see a partial eclipse.'],
  ['2032-05-09T13:26:42', 'annular', 'South Atlantic',
    'A brief ring of fire over the South Atlantic. Southern South America and southern Africa see a partial eclipse.'],
  ['2032-11-03T05:34:12', 'partial', 'Asia',
    'The Moon covers up to 86% of the Sun, seen from Asia.'],
];

// [greatest eclipse (TT), type, umbral magnitude, totality minutes, where]
const LUNAR = [
  ['2026-03-03T11:34:52', 'total', 1.151, 58, 'East Asia, Australia, the Pacific, the Americas'],
  ['2026-08-28T04:14:04', 'partial', 0.930, 0, 'The Americas, Europe, Africa'],
  ['2028-01-12T04:14:13', 'partial', 0.066, 0, 'The Americas, Europe, Africa'],
  ['2028-07-06T18:20:57', 'partial', 0.389, 0, 'Europe, Africa, Asia, Australia'],
  ['2028-12-31T16:53:15', 'total', 1.246, 71, 'Europe, Africa, Asia, Australia'],
  ['2029-06-26T03:23:22', 'total', 1.844, 102, 'The Americas, Europe, Africa, the Middle East'],
  ['2029-12-20T22:43:12', 'total', 1.117, 54, 'The Americas, Europe, Africa, Asia'],
  ['2030-06-15T18:34:34', 'partial', 0.502, 0, 'Europe, Africa, Asia, Australia'],
  ['2032-04-25T15:14:51', 'total', 1.191, 66, 'East Africa, Asia, Australia, the Pacific'],
  ['2032-10-18T19:03:40', 'total', 1.103, 47, 'Africa, Europe, Asia, Australia'],
];

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const ttToUtc = (tt) => new Date(Date.parse(`${tt}Z`) - DELTA_T_S * 1000);

function solarEvent([tt, type, where, detail]) {
  const at = ttToUtc(tt);
  return {
    id: `eclipse-solar-${isoDay(at)}`,
    kind: 'eclipse-solar',
    body: 'sun',
    title: `${cap(type)} solar eclipse`,
    at: at.toISOString(),
    short: `${dayText(at)} · ${where}`,
    detail: `${detail} Never look at the Sun without eclipse glasses.`,
    major: type !== 'partial',
  };
}

function lunarEvent([tt, type, mag, totalMin, where]) {
  const at = ttToUtc(tt);
  const seen = `Seen from wherever the Moon is up: ${where.replace(/^The /, 'the ')}.`;
  const span = totalMin >= 60 ? `1 hour ${totalMin - 60} minutes` : `${totalMin} minutes`;
  const what = type === 'total'
    ? `The Moon turns a deep red for ${span} of totality.`
    : mag < 0.15
      ? 'Earth\'s shadow just clips the edge of the Moon.'
      : `Earth's shadow covers ${Math.round(mag * 100)}% of the Moon's width at most.`;
  return {
    id: `eclipse-lunar-${isoDay(at)}`,
    kind: 'eclipse-lunar',
    body: 'moon',
    title: `${cap(type)} lunar eclipse`,
    at: at.toISOString(),
    short: `${dayText(at)} · ${where.replace(/^The /, '')}`,
    detail: `${what} ${seen}`,
    major: type === 'total',
  };
}

const ECLIPSES = [...SOLAR.map(solarEvent), ...LUNAR.map(lunarEvent)];

// ---- The Sun and the Moon from Earth ------------------------------------------

// The Sun's geocentric ecliptic longitude (degrees, J2000 ecliptic) from the
// Earth-Moon barycentre; the barycentre's offset is a few arcseconds.
function sunLongitude(jd) {
  const e = getPlanetPosition('earth', jd);
  return ((Math.atan2(-e.y, -e.x) / DEG) % 360 + 360) % 360;
}

// The Moon's lit fraction and whether it is waxing. moon.js is the ecliptic
// of date, so the Sun is precessed to match (about 1.4 degrees a century).
function moonPhase(jd) {
  const T = (jd - J2000) / 36525;
  const ls = (sunLongitude(jd) + 1.3969713 * T) * DEG;
  const m = moonEcliptic(jd);
  const cosPsi = Math.cos(m.lat) * Math.cos(m.lon - ls);
  return { lit: (1 - cosPsi) / 2, waxing: wrap180((m.lon - ls) / DEG) > 0 };
}

// ---- Meteor showers -------------------------------------------------------------
// IMO working list: peak solar longitude (J2000), the usual peak date to
// search around, ZHR, and what to expect.

const SHOWERS = [
  { key: 'quadrantids', name: 'Quadrantid', lon: 283.15, month: 0, day: 3, zhr: 80, major: true,
    note: 'Best from the Northern Hemisphere in the hours before dawn. The peak lasts only a few hours.' },
  { key: 'lyrids', name: 'Lyrid', lon: 32.32, month: 3, day: 22, zhr: 18, major: false,
    note: 'Best after midnight from the Northern Hemisphere, with the odd bright fireball.' },
  { key: 'eta-aquariids', name: 'Eta Aquariid', lon: 45.5, month: 4, day: 6, zhr: 50, major: false,
    note: 'Dust from Halley\'s Comet, best from the Southern Hemisphere and the tropics in the hour or two before dawn.' },
  { key: 'perseids', name: 'Perseid', lon: 140.0, month: 7, day: 12, zhr: 100, major: true,
    note: 'Best after midnight from the Northern Hemisphere, on warm summer nights.' },
  { key: 'orionids', name: 'Orionid', lon: 208.0, month: 9, day: 21, zhr: 20, major: false,
    note: 'Dust from Halley\'s Comet, seen from both hemispheres after midnight.' },
  { key: 'leonids', name: 'Leonid', lon: 235.27, month: 10, day: 17, zhr: 15, major: false,
    note: 'Fast meteors, best in the hours before dawn.' },
  { key: 'geminids', name: 'Geminid', lon: 262.2, month: 11, day: 14, zhr: 150, major: true,
    note: 'The richest shower of the year, with meteors from mid-evening onwards and the most after midnight.' },
];

// When the Sun reaches lon in a given year: bisection within five days of
// the usual date (the Sun moves about a degree a day).
function solarLongitudeTime(lon, year, month, day) {
  const mid = jdOf(Date.UTC(year, month, day, 12));
  let a = mid - 5, b = mid + 5;
  const f = (jd) => wrap180(sunLongitude(jd) - lon);
  if (f(a) > 0 || f(b) < 0) return null;
  for (let i = 0; i < 40; i++) {
    const m = (a + b) / 2;
    if (f(m) < 0) a = m; else b = m;
  }
  return (a + b) / 2;
}

// "an 86% Moon", "a 25% Moon": eight, eighty-odd, eleven and eighteen
// start with a vowel.
const aPct = (pct) => `${/^8/.test(String(pct)) || pct === 11 || pct === 18 ? 'An' : 'A'} ${pct}%`;

function moonNote({ lit, waxing }) {
  const pct = Math.round(lit * 100);
  if (pct <= 3) return 'There is no Moon that night, so the sky stays dark.';
  if (lit < 0.12) return `A thin ${pct}% Moon leaves the sky dark.`;
  if (lit < 0.35) return `${aPct(pct)} crescent Moon is low and faint, so the sky stays fairly dark.`;
  if (lit >= 0.85) return `${aPct(pct)} Moon washes out all but the brightest meteors.`;
  if (waxing) return `${aPct(pct)} Moon sets during the night, leaving the hours before dawn dark.`;
  return `${aPct(pct)} Moon rises during the night and brightens the hours before dawn, so watch before it comes up.`;
}

function showerEvent(s, year) {
  const jd = solarLongitudeTime(s.lon, year, s.month, s.day);
  if (jd == null) return null;
  const at = new Date(Math.round(msOf(jd) / 60000) * 60000);
  return {
    id: `meteor-${s.key}-${year}`,
    kind: 'meteor',
    body: 'earth',
    title: `${s.name} meteor shower peak`,
    at: at.toISOString(),
    short: `${dayText(at)} · up to ${s.zhr} an hour`,
    detail: `Up to about ${s.zhr} meteors an hour from a dark site. ${s.note} ${moonNote(moonPhase(jd))}`,
    major: s.major,
  };
}

// ---- Oppositions ----------------------------------------------------------------
// The planet's geocentric ecliptic longitude 180 degrees from the Sun's.

const OUTER = {
  mars: { name: 'Mars', major: true, see: 'It rises at sunset, is up all night and shines at its brightest as an orange-red star.' },
  jupiter: { name: 'Jupiter', major: true, see: 'It rises at sunset, is up all night and shines at its brightest; binoculars show its four big moons.' },
  saturn: { name: 'Saturn', major: true, see: 'It rises at sunset, is up all night and shines at its brightest; a small telescope shows the rings.' },
  uranus: { name: 'Uranus', major: false, see: 'It is up all night at its brightest, a faint star for binoculars from a dark site.' },
  neptune: { name: 'Neptune', major: false, see: 'It is up all night at its brightest, a faint star for binoculars, with a telescope showing a small blue disc.' },
};

function oppositionOffset(body, jd) {
  const e = getPlanetPosition('earth', jd);
  const p = getPlanetPosition(body, jd);
  const planetLon = Math.atan2(p.y - e.y, p.x - e.x) / DEG;
  return wrap180(planetLon - sunLongitude(jd) - 180);
}

// Every opposition of body in the year: daily steps, then bisection where
// the offset falls through zero (the Sun gains on the planet, so the offset
// shrinks; the jump at conjunction goes the other way and is skipped).
function oppositionsInYear(body, year) {
  const out = [];
  const start = jdOf(Date.UTC(year, 0, 1)) - 1;
  const end = jdOf(Date.UTC(year + 1, 0, 1)) + 1;
  let prevJd = start, prev = oppositionOffset(body, start);
  for (let jd = start + 1; jd <= end; jd += 1) {
    const cur = oppositionOffset(body, jd);
    if (prev > 0 && cur <= 0 && prev - cur < 90) {
      let a = prevJd, b = jd;
      for (let i = 0; i < 40; i++) {
        const m = (a + b) / 2;
        if (oppositionOffset(body, m) > 0) a = m; else b = m;
      }
      const t = (a + b) / 2;
      const at = new Date(Math.round(msOf(t) / 60000) * 60000);
      if (at.getUTCFullYear() === year) out.push({ jd: t, at });
    }
    prevJd = jd;
    prev = cur;
  }
  return out;
}

function oppositionEvent(body, { jd, at }) {
  const o = OUTER[body];
  const km = distanceFromEarthKm(body, jd);
  return {
    id: `opposition-${body}-${at.getUTCFullYear()}`,
    kind: 'opposition',
    body,
    title: `${o.name} at opposition`,
    at: at.toISOString(),
    short: `${dayText(at)} · ${formatMillionKm(km)} away`,
    detail: `${o.name} sits opposite the Sun in the sky. ${o.see} It will be ${formatMillionKm(km)} from Earth.`,
    major: o.major,
  };
}

// ---- The calendar -----------------------------------------------------------------

const byYear = new Map();

export function skyEventsInYear(year) {
  if (byYear.has(year)) return byYear.get(year);
  const events = [
    ...ECLIPSES.filter((e) => new Date(e.at).getUTCFullYear() === year),
    ...SHOWERS.map((s) => showerEvent(s, year)).filter(Boolean),
    ...Object.keys(OUTER).flatMap((b) => oppositionsInYear(b, year).map((o) => oppositionEvent(b, o))),
  ].sort((a, b) => a.at.localeCompare(b.at));
  byYear.set(year, events);
  return events;
}

export function upcomingSkyEvents({ from = new Date(), days = 450 } = {}) {
  const t0 = new Date(from).getTime();
  const t1 = t0 + days * DAY_MS;
  const out = [];
  for (let y = new Date(t0).getUTCFullYear(); y <= new Date(t1).getUTCFullYear(); y++) {
    for (const e of skyEventsInYear(y)) {
      const t = Date.parse(e.at);
      if (t >= t0 && t <= t1) out.push(e);
    }
  }
  return out;
}

// The year sits in every id (the eclipse ids carry the full date), so the
// lookup only builds that year.
export function skyEventById(id) {
  const m = /-(\d{4})(?:-\d{2}-\d{2})?$/.exec(String(id || ''));
  if (!m) return null;
  return skyEventsInYear(Number(m[1])).find((e) => e.id === id) || null;
}

// Lunar eclipses can be nearly two years apart, so the search runs 800 days.
export function nextSkyEventFor(body, { from = new Date() } = {}) {
  return upcomingSkyEvents({ from, days: 800 }).find((e) => e.body === body) || null;
}
